import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { opcionesAPI, type PuntoVenta, type Sucursal } from "@/lib/api";
import { NumberField } from "@/components/ui/number-field";

interface FormData {
  codigo: string;
  nombre: string;
  sucursal: string;
  afipPuntoVenta: number;
}

const emptyForm: FormData = {
  codigo: "",
  nombre: "",
  sucursal: "",
  afipPuntoVenta: 0
};

interface PuntoVentaABMProps {
  selectedItem?: PuntoVenta | null;
  onBack?: () => void;
  onSaved?: () => void;
}

export function PuntoVentaABM({ selectedItem, onBack, onSaved: _onSaved }: PuntoVentaABMProps) {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [originalFormData, setOriginalFormData] = useState<FormData>(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredPuntosVenta, setFilteredPuntosVenta] = useState<PuntoVenta[]>([]);
  const [allPuntosVenta, setAllPuntosVenta] = useState<PuntoVenta[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pvs, sucs] = await Promise.all([
          opcionesAPI.listPuntosVenta(),
          opcionesAPI.listSucursales()
        ]);
        setAllPuntosVenta(pvs);
        setSucursales(sucs);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Error al cargar datos");
      }
    };
    loadData();
  }, []);

  // Load selectedItem when passed from parent
  useEffect(() => {
    if (selectedItem) {
      selectPuntoVenta(selectedItem);
    } else {
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
    }
  }, [selectedItem]);

  const handleNombreChange = (value: string) => {
    setFormData({ ...formData, nombre: value });
    checkForChanges({ ...formData, nombre: value });

    if (value.trim()) {
      const filtered = allPuntosVenta.filter(pv =>
        pv.nombre?.toLowerCase().includes(value.toLowerCase()) ||
        pv.codigo?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPuntosVenta(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectPuntoVenta = (pv: PuntoVenta) => {
    const loadedData: FormData = {
      codigo: pv.codigo,
      nombre: pv.nombre,
      sucursal: String(pv.sucursal || ""),
      afipPuntoVenta: pv.afipPuntoVenta || 0
    };
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setHasChanges(false);
    setShowSuggestions(false);
    setIsEditing(true);
  };

  const handleNombreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (filteredPuntosVenta.length > 0) {
        selectPuntoVenta(filteredPuntosVenta[0]);
      } else if (formData.nombre.trim() && !isEditing) {
        setHasChanges(true);
        setShowSuggestions(false);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const checkForChanges = (data: FormData) => {
    const changed = JSON.stringify(data) !== JSON.stringify(originalFormData);
    setHasChanges(changed);
  };

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    checkForChanges(newData);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.sucursal) {
      toast.error("La sucursal es requerida");
      return;
    }

    setLoading(true);
    try {
      const sucursalId = parseInt(formData.sucursal);

      if (isEditing && formData.codigo) {
        await opcionesAPI.updatePuntoVenta(formData.codigo, {
          nombre: formData.nombre,
          sucursal: sucursalId,
          afipPuntoVenta: formData.afipPuntoVenta || undefined,
        });
        toast.success("Punto de venta actualizado");
      } else {
        const newPV = await opcionesAPI.createPuntoVenta({
          codigo: "",
          nombre: formData.nombre,
          sucursal: sucursalId,
          afipPuntoVenta: formData.afipPuntoVenta || undefined,
          activo: true,
        });
        setFormData({ ...formData, codigo: newPV.codigo });
        setIsEditing(true);
        toast.success("Punto de venta creado");
      }

      const updated = await opcionesAPI.listPuntosVenta();
      setAllPuntosVenta(updated);
      setOriginalFormData(formData);
      setHasChanges(false);
    } catch (error: any) {
      console.error("Error saving punto venta:", error);
      toast.error(error.message || "Error al guardar punto de venta");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await opcionesAPI.deletePuntoVenta(formData.codigo);
      toast.success("Punto de venta eliminado");

      const updated = await opcionesAPI.listPuntosVenta();
      setAllPuntosVenta(updated);
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
      setShowDeleteDialog(false);
    } catch (error: any) {
      console.error("Error deleting punto venta:", error);
      toast.error(error.message || "Error al eliminar punto de venta");
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setOriginalFormData(emptyForm);
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleNew}>
          Nuevo Punto de Venta
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleNombreChange(e.target.value)}
            onKeyDown={handleNombreKeyDown}
            onFocus={() => {
              if (formData.nombre.trim() && filteredPuntosVenta.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Escriba para buscar o crear..."
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {showSuggestions && filteredPuntosVenta.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredPuntosVenta.map((pv) => (
                <button
                  key={pv.codigo}
                  type="button"
                  onClick={() => selectPuntoVenta(pv)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex justify-between items-center"
                >
                  <span>{pv.nombre}</span>
                  <span className="text-xs text-muted-foreground">{pv.codigo}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Sucursal</label>
          <Select
            value={formData.sucursal}
            onValueChange={(value) => handleInputChange("sucursal", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Seleccione sucursal" />
            </SelectTrigger>
            <SelectContent>
              {sucursales.map((suc) => (
                <SelectItem key={suc.codigo} value={String(suc.id || suc.codigo)}>
                  {suc.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ARCA Punto de Venta</label>
          <NumberField
            min="0"
            value={formData.afipPuntoVenta}
            onChange={(e) => handleInputChange("afipPuntoVenta", Number(e.target.value))}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            placeholder="Ej: 1"
          />
        </div>
      </div>
      <FormFooter
        onBack={onBack}
        onDelete={handleDelete}
        deleteDisabled={!formData.codigo || loading}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saving={loading}
      />
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar este punto de venta?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}